<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, SelectInput, UiModalHeader } from '@/shared/ui';
  import type { TrainingMatrixRevisionListItem } from '@/entities/Training';
  import { buildTrainingMatrixRevisionOptionLabel } from '../lib/buildTrainingMatrixRevisionOptionLabel';

  interface Props {
    revisions: TrainingMatrixRevisionListItem[];
    loading?: boolean;
    selectedRevisionId: number | null;
  }

  const props = withDefaults(defineProps<Props>(), {
    loading: false,
  });

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'confirm'): void;
    (e: 'select', revisionId: number): void;
  }>();

  const options = computed(() =>
    props.revisions.map((revision) => ({
      id: revision.id,
      name: buildTrainingMatrixRevisionOptionLabel(revision),
    }))
  );

  const placeholderLabel = computed(() => {
    if (props.loading) return 'Carregando revisões...';
    if (props.revisions.length === 0) return 'Nenhuma revisão cadastrada';
    return 'Selecione uma revisão';
  });

  function handleSelect(value: string | number): void {
    emit('select', Number(value));
  }
</script>

<template>
  <VueFinalModal
    :model-value="true"
    class="flex items-center justify-center"
    content-class="w-[95vw] max-w-[432px] overflow-hidden rounded-2xl bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="
      (value: boolean) => {
        if (!value) emit('close');
      }
    "
  >
    <div class="flex flex-col">
      <div class="border-b border-gray-02 px-6 pb-3 pt-4">
        <UiModalHeader
          title="Gerar Matriz"
          text-color="gray-07"
          title-size="text-sm"
          @close="emit('close')"
        />
      </div>

      <div class="border-b border-gray-02 px-6 py-6">
        <label class="mb-2 block text-sm font-medium text-gray-11">Revisão da Matriz:</label>
        <SelectInput
          :model-value="selectedRevisionId ?? ''"
          :options="options"
          :label="placeholderLabel"
          :disabled="loading || revisions.length === 0"
          use-teleport
          @update:model-value="handleSelect"
        />
      </div>

      <div class="flex justify-end gap-4 p-5">
        <ColoredButton
          data-testid="modal-cancel"
          outlined
          color="gray-03"
          border-color="gray-03"
          text="Cancelar"
          @click="emit('close')"
        />
        <ColoredButton
          data-testid="modal-confirm"
          text="Gerar Matriz"
          :disabled="selectedRevisionId === null"
          @click="emit('confirm')"
        />
      </div>
    </div>
  </VueFinalModal>
</template>
